import { useQuery } from '@tanstack/react-query'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Bot,
  FileText,
  LayoutDashboard,
  type LucideIcon,
  Mail,
  Palette,
  Shield,
  Smartphone,
  User as UserIcon,
  Video,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { api } from '../lib/api'
import { useMobilePageTitle } from './shell/mobile-shell-outlets'
import { PageHeader } from './ui/page-header'
import { TabNav, tabPillClass } from './ui/tab-nav'

interface SettingsTab {
  to: string
  label: string
  icon: LucideIcon
  adminOnly?: boolean
  group?: string
}

const TABS: SettingsTab[] = [
  { to: '/settings/mail-accounts', label: 'Mail-Konten', icon: Mail },
  { to: '/settings/profile', label: 'Profil', icon: UserIcon },
  { to: '/settings/invitations', label: 'Mitarbeiter', icon: UserIcon, adminOnly: true },
  { to: '/settings/integrations', label: 'Integrationen', icon: Smartphone },
  { to: '/settings/appearance', label: 'Design', icon: Palette },
  { to: '/settings/invoice-layout', label: 'Rechnungs-Layout', icon: FileText, adminOnly: true },
  { to: '/settings/meeting-server', label: 'Meeting-Server', icon: Video, adminOnly: true },
  {
    to: '/settings/dashboard-templates',
    label: 'Dashboard-Vorlagen',
    icon: LayoutDashboard,
    adminOnly: true,
  },
  { to: '/settings/ai', label: 'KI-Assistent', icon: Bot, adminOnly: true },
  { to: '/settings/roles', label: 'Rollen', icon: Shield, adminOnly: true },
]

export function SettingsLayout({
  title,
  // Bewusst nicht mehr gerendert (Ticket „obere Leiste einreihig"): die
  // Beschreibungszeile des alten Serif-Kopfs entfällt zugunsten der einen
  // kompakten Kopfzeile. Prop bleibt, bis die Routen-Aufrufer bereinigt sind.
  description: _description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  const { data: meData } = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.me() })
  const location = useRouterState({ select: (s) => s.location.pathname })
  const isAdmin = meData?.user?.role === 'admin'
  // Mobile-Shell (<640): EIN Titel für alle ~10 Settings-Routen — der
  // Seiten-Kontext steckt im aktiven Tab der Subnav-Zeile (bleibt im
  // Content, User-Entscheid: kein Sheet).
  useMobilePageTitle('Einstellungen')

  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin)

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Einreihiger Standard-Kopf (Ticket): Eyebrow ersetzt die alte
          Breadcrumb-Zeile, die Tab-Pills wandern in die Kopfzeile (Muster
          Stundenkonten). keep-row: die Tabs sind am Phone die einzige
          Settings-Navigation — nur der Titel wandert in die Shell-Leiste. */}
      <PageHeader
        title={title}
        eyebrow="Einstellungen"
        phoneMode="keep-row"
        inline={
          <TabNav ariaLabel="Einstellungen-Bereiche">
            {visibleTabs.map((tab) => {
              const active = location === tab.to || location.startsWith(`${tab.to}/`)
              const Icon = tab.icon
              return (
                <Link key={tab.to} to={tab.to} className={tabPillClass(active)}>
                  <Icon className="h-3.5 w-3.5" strokeWidth={active ? 2.25 : 2} />
                  {tab.label}
                </Link>
              )
            })}
          </TabNav>
        }
      />

      {/* Content */}
      <div>{children}</div>
    </div>
  )
}
